<template>
  <div>
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard label="อัตราการมีงานทำ" :value="`${employmentSummary.employment_rate}%`" align="left">
        <template #icon>
          <div class="w-6 h-6">
            <BagIcon class="w-full h-full" />
          </div>
        </template>
      </StatCard>
      <StatCard label="รายได้เฉลี่ย บาท/เดือน" :value="employmentSummary.avg_income_per_month" align="left">
        <template #icon>
          <div class="w-6 h-6">
            <DollarSignBagIcon class="w-full h-full" />
          </div>
        </template>
      </StatCard>
      <StatCard label="มีหนี้สิน" :value="`${employmentSummary.pct_with_debt}%`" align="left">
        <template #icon>
          <div class="w-6 h-6">
            <DeptAltIcon class="w-full h-full" />
          </div>
        </template>
      </StatCard>
      <StatCard label="มีเงินออม" :value="`${employmentSummary.pct_with_saving}%`" align="left">
        <template #icon>
          <div class="w-6 h-6">
            <SavingBagIcon class="w-full h-full" />
          </div>
        </template>
      </StatCard>
    </div>

    <div class="grid grid-cols-12 gap-6 mt-6">
      <div class="col-span-12 lg:col-span-5">
        <ChartCard title="สถานะการทำงาน">
          <div class="h-[400px]">
            <ChartWithLegend
              chart-id="pwWorkStatusChart"
              :series="workStatus.series"
              :labels="workStatus.labels"
              unit="คน"
              :height="200"
              :show-data-label="true"
              :palette="workStatusPalette"
            />
          </div>
        </ChartCard>
      </div>
      <div class="col-span-12 lg:col-span-7">
        <ChartCard title="สถานภาพการทำงาน">
          <div class="h-[400px]">
            <DashboardBarEChart
              chart-id="pwWorkTypeChart"
              type="bar"
              :data="workTypeData"
              :options="redBarOptions"
              fill-height
            />
          </div>
        </ChartCard>
      </div>
    </div>

    <div class="grid grid-cols-12 gap-6 mt-6">
      <div class="col-span-12 lg:col-span-7">
        <ChartCard title="สาเหตุหลักของการว่างงาน">
          <div class="h-[400px]">
            <DashboardBarEChart
              chart-id="pwUnemployedChart"
              type="bar"
              :data="unemployedData"
              :options="navyBarOptions"
              fill-height
            />
          </div>
        </ChartCard>
      </div>
      <div class="col-span-12 lg:col-span-5">
        <ChartCard title="ประเภทของค่าจ้างและเงินเดือน">
          <div class="min-h-[400px]">
            <ProgressList :items="wageTypes" :color="P.yellow" minHeight="400px" />
          </div>
        </ChartCard>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import StatCard from "../../container/StatCard.vue";
import ChartCard from "../../container/ChartCard.vue";
import ChartWithLegend from "../../container/ChartWithLegend.vue";
import ProgressList from "../../container/ProgressList.vue";
import DashboardBarEChart from "../../container/DashboardBarEChart.vue";
import SavingBagIcon from "@/global-components/icons/SavingBagIcon.vue";
import { globalNumDecFixed } from "@/utils/format-number.js";

const props = defineProps({
  data: { type: Object, default: () => ({}) },
});

const P = { navy: "#3A506A", blue: "#20A0D7", yellow: "#F4D35E", green: "#11D67A", red: "#F73C4B" };

const bar = (label, color, data, opts = {}) => ({
  label, backgroundColor: color, data, barPercentage: 0.6, categoryPercentage: 0.8, ...opts,
});

const barOptions = ({ legend = false, stacked = false, horizontal = false, aspectRatio = 2 } = {}) => {
  const cat = { stacked, grid: { display: false }, ticks: { font: { size: 12 }, autoSkip: false, maxRotation: horizontal ? 0 : 45, minRotation: horizontal ? 0 : 45 } };
  const val = { stacked, beginAtZero: true, grid: { color: "#E2E8F0" }, ticks: { font: { size: 12 }, precision: 0 } };
  return {
    indexAxis: horizontal ? "y" : "x",
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio,
    plugins: {
      legend: { display: legend, position: "bottom", labels: { usePointStyle: true, boxWidth: 8, font: { size: 12 } } },
      tooltip: {
        enabled: true,
        callbacks: {
          label(ctx) {
            const numVal = horizontal ? ctx.parsed.x : ctx.parsed.y;
            return `${ctx.dataset.label}: ${globalNumDecFixed(numVal)} คน`;
          },
        },
      },
    },
    scales: horizontal ? { x: val, y: cat } : { x: cat, y: val },
  };
};

const toNumber = (value) => Number(value || 0);

const employmentSummary = computed(() => {
  const s = props.data?.employment_summary || {};
  return {
    employment_rate: globalNumDecFixed(toNumber(s.employment_rate)),
    avg_income_per_month: globalNumDecFixed(toNumber(s.avg_income_per_month)),
    pct_with_debt: globalNumDecFixed(toNumber(s.pct_with_debt)),
    pct_with_saving: globalNumDecFixed(toNumber(s.pct_with_saving)),
  };
});

const workStatus = computed(() => {
  const items = props.data?.work_status || []
  if (items.length) {
    return {
      labels: items.map((item) => item.name || ""),
      series: items.map((item) => toNumber(item.total)),
    };
  }
  return {
    labels: [],
    series: [],
  }
});
const WORK_STATUS_COLOR_BY_LABEL = { "ว่างงาน/ไม่ได้ทำงาน": "#F73C4B", "มีงานทำ": "#11D67A", "มีงานทำ แต่เป็นการทำงานชั่วคราว": "#F4D35E" };
const workStatusPalette = computed(() =>
  workStatus.value.labels.map((label) => WORK_STATUS_COLOR_BY_LABEL[label] || "#606468")
);

const workTypeData = computed(() => {
  const items = props.data?.work_type || []
  const occupationDisplay = [
    { key: "d1", display: ["รับจ้างทำงานแบบชั่วคราวในระยะ", "เวลาสั้น ๆ และจบเป็นครั้งคราว"] },
    { key: "d2", display: ["ทำธุรกิจโดยไม่มีลูกจ้าง"] },
    { key: "d3", display: ["นายจ้าง (ทำธุรกิจโดยมีลูกจ้างช่วยงาน", "รวมธุรกิจที่เกี่ยวข้องกับการเกษตร", "และไม่ใช่การเกษตร)"] },
    { key: "d4", display: ["ช่วยธุรกิจในครัวเรือนโดยไม่ได้รับค่าจ้าง"] },
    { key: "d5", display: ["ลูกจ้างรายวันของบริษัท ห้างร้าน ", "ของภาคเอกชน"] },
    { key: "d6", display: ["พนักงาน/ลูกจ้างประจำของบริษัท ", "ห้างร้าน ของภาคเอกชน"] },
    { key: "d7", display: ["ข้าราชการ/พนักงานราชการ/พนักงาน", "รัฐวิสาหกิจ/ลูกจ้างประจำของรัฐ/", "ลูกจ้างประจำของรัฐวิสาหกิจ"] },
    { key: "d8", display: ["ลูกจ้างรายวันของรัฐ หรือรัฐวิสาหกิจ"] },
    { key: "d9", display: ["ลูกจ้างองค์กรหรือวิสาหกิจชุมชน/", "การรวมกลุ่มในชุมชน"] },
    { key: "d10", display: ["ลูกจ้างแพลตฟอร์ม ผู้ขับรถ", "ส่งของหรือไรเดอร์"] },
    { key: "other", display: ["อื่น ๆ"] }
  ];
  const occupationMap = {
    "รับจ้างทำงานแบบชั่วคราวในระยะเวลาสั้น ๆ และจบเป็นครั้งคราว": "d1",
    "ทำธุรกิจโดยไม่มีลูกจ้าง": "d2",
    "นายจ้าง (ทำธุรกิจโดยมีลูกจ้างช่วยงาน รวมธุรกิจที่เกี่ยวข้องกับการเกษตรและไม่ใช่การเกษตร)": "d3",
    "ช่วยธุรกิจในครัวเรือนโดยไม่ได้รับค่าจ้าง": "d4",
    "ลูกจ้างรายวันของบริษัท ห้างร้าน ของภาคเอกชน": "d5",
    "พนักงาน/ลูกจ้างประจำของบริษัท ห้างร้าน ของภาคเอกชน": "d6",
    "ข้าราชการ/พนักงานราชการ/พนักงานรัฐวิสาหกิจ/ลูกจ้างประจำของรัฐ/ลูกจ้างประจำของรัฐวิสาหกิจ": "d7",
    "ลูกจ้างรายวันของรัฐ หรือรัฐวิสาหกิจ": "d8",
    "ลูกจ้างองค์กรหรือวิสาหกิจชุมชน/การรวมกลุ่มในชุมชน": "d9",
    "ลูกจ้างแพลตฟอร์ม ผู้ขับรถส่งของหรือไรเดอร์": "d10",
    "อื่น ๆ": "other",
    "อื่นๆ": "other"
  };
  if (items.length) {
    return {
      labels: occupationDisplay.map((item) => item.display),
      datasets: [
        bar(
          "จำนวนคน",
          "#4e6aa0",
          occupationDisplay.map((d) =>
            toNumber(
              items.find(
                (item) => occupationMap[(item.name || "")] === d.key
              )?.total
            )
          ),
          { barPercentage: 0.4 }
        )
      ],
    };
  }
  return {
    labels: [],
    datasets: [],
  }
});

const unemployedData = computed(() => {
  const items = props.data?.unemployment_cause || []
  const unemployedDisplay = [
    { key: "d1", display: ["ว่างงาน เพราะต้องดูแลครอบครัว"] },
    { key: "d2", display: ["ว่างงาน เพราะรอฤดูกาล"] },
    { key: "d3", display: ["ว่างงาน เพราะป่วย/พิการ"] },
    { key: "d4", display: ["ไม่ได้ทำงาน เพราะกำลังศึกษาอยู่"] },
    { key: "d5", display: ["ว่างงาน เพราะกำลังหางานทำ"] },
    { key: "d6", display: ["ว่างงาน เพราะไม่สมัครใจทำงาน"] },
    { key: "d7", display: ["ว่างงาน เพราะชราภาพ/เกษียณอายุ"] },
    { key: "other", display: ["อื่น ๆ"] }
  ];
  const unemployedMap = {
    "ว่างงาน เพราะต้องดูแลครอบครัว": "d1",
    "ว่างงาน เพราะรอฤดูกาล": "d2",
    "ว่างงาน เพราะป่วย/พิการ": "d3",
    "ไม่ได้ทำงาน เพราะกำลังศึกษาอยู่": "d4",
    "ว่างงาน เพราะกำลังหางานทำ": "d5",
    "ว่างงาน เพราะไม่สมัครใจทำงาน": "d6",
    "ว่างงาน เพราะชราภาพ/เกษียณอายุ": "d7",
    "อื่น ๆ": "other",
    "อื่นๆ": "other"
  };
  if (items.length) {
    return {
      labels: unemployedDisplay.map((item) => item.display),
      datasets: [
        bar(
          "จำนวนคน",
          "#f17982",
          unemployedDisplay.map((d) =>
            toNumber(
              items.find(
                (item) => unemployedMap[(item.name || "")] === d.key
              )?.total
            )
          ),
          { barPercentage: 0.4 }
        )
      ],
    };
  }
  return {
    labels: [],
    datasets: [],
  }
});
const wageTypes = computed(() => {
  const items = props.data?.wage_type || [];
  if (items.length) {
    return items.map((item) => ({
      label: item.name || "",
      percent: item.percentage || 0,
      value: toNumber(item.total),
    }));
  }
  return []
});


const redBarOptions = barOptions({ aspectRatio: 1.8 });
const navyBarOptions = barOptions({ aspectRatio: 1.8 });
</script>
